import React, { useState, useEffect, useCallback, useRef } from 'react';
import { FaDiscord } from 'react-icons/fa';

const DISCORD_INVITE_URL = 'https://discord.gg/uzplaylists';

const TIERS = [
  {
    key: 'free',
    name: 'free',
    price: '$0',
    priceSuffix: 'forever',
    features: [
      'discord access',
      'community chat',
      "see what's on rotation",
    ],
    cta: 'join the discord',
    ctaIcon: 'discord',
    backendKey: null,
    badge: null,
    featured: false,
  },
  {
    key: 'music-lover',
    name: 'music lover',
    price: '$8',
    priceSuffix: '/ month',
    features: [
      'everything in free',
      'pick a playlist to co-curate',
      '25% off submissions + shop',
      'free bi-weekly dj mixes',
      'access to the weekly playlist archive',
      '3× vote weight on submissions',
      'behind-the-scenes channel',
      'custom discord role',
    ],
    cta: 'become a music lover',
    ctaIcon: null,
    backendKey: 'member',
    badge: 'most popular',
    featured: true,
  },
  {
    key: 'artist',
    name: 'artist',
    price: '$15',
    priceSuffix: '/ month',
    features: [
      'everything in music lover',
      '2 free express submissions / month ($60 value)',
      'auto-share your submission to discord',
      'custom discord role',
    ],
    cta: 'become an artist',
    ctaIcon: null,
    backendKey: 'artist',
    badge: null,
    featured: false,
  },
];

const PricingPage = () => {
  // Read ?resume=<tier> on load — set by the OAuth flow when an
  // unauthenticated user picks a paid tier and gets bounced to Discord login.
  // We preselect that tier and auto-fire checkout once they return.
  const initialResume = (() => {
    try {
      const params = new URLSearchParams(window.location.search);
      const r = params.get('resume');
      return r && TIERS.some((t) => t.key === r && t.backendKey) ? r : null;
    } catch {
      return null;
    }
  })();

  const [selectedKey, setSelectedKey] = useState(initialResume || 'music-lover');
  const [isProcessing, setIsProcessing] = useState(Boolean(initialResume));
  const [error, setError] = useState(null);
  const autoResumedRef = useRef(false);

  const selectedTier = TIERS.find((t) => t.key === selectedKey) || TIERS[1];

  const startCheckout = useCallback(async (tier) => {
    setError(null);
    setIsProcessing(true);
    try {
      const response = await fetch('/api/payments/memberships/join/', {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRFToken': window.djangoContext?.csrfToken,
        },
        body: JSON.stringify({ plan: tier.backendKey }),
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.error || 'Failed to start checkout');

      if (!data.authenticated) {
        // Not signed in — bounce to Discord OAuth and come back to this
        // page with ?resume=<tier> so we can resume from where we left off.
        const resumeUrl = `/membership?resume=${tier.key}`;
        window.location.href = `/oauth/login/discord/?next=${encodeURIComponent(resumeUrl)}`;
        return;
      }
      if (data.checkout_url) {
        window.location.href = data.checkout_url;
        return;
      }
      throw new Error('No checkout URL returned');
    } catch (err) {
      console.error('Checkout error:', err);
      setError(err.message || 'Something went wrong. Try again.');
      setIsProcessing(false);
    }
  }, []);

  // On return from Discord OAuth (?resume=<tier> in URL), auto-fire checkout
  useEffect(() => {
    if (!initialResume || autoResumedRef.current) return;
    autoResumedRef.current = true;
    const tier = TIERS.find((t) => t.key === initialResume);
    if (tier && tier.backendKey) {
      // Clean the query string so a refresh doesn't re-fire
      try {
        const url = new URL(window.location.href);
        url.searchParams.delete('resume');
        window.history.replaceState({}, '', url.toString());
      } catch {}
      startCheckout(tier);
    }
  }, [initialResume, startCheckout]);

  const handleCTA = () => {
    if (isProcessing) return;
    // Free tier — direct to Discord server invite, no auth needed
    if (selectedTier.backendKey === null) {
      window.location.href = DISCORD_INVITE_URL;
      return;
    }
    startCheckout(selectedTier);
  };

  return (
    <>
      <div className="min-h-screen bg-white-pure relative texture-halftone-light overflow-hidden">
        {/* Rotating halftone circle — background decoration (matches homepage) */}
        <div className="absolute -right-[20%] top-[15%] w-[600px] h-[600px] md:w-[800px] md:h-[800px] pointer-events-none select-none z-0">
          <div className="w-full h-full animate-spin-slow origin-center">
            <svg viewBox="0 0 400 400" className="w-full h-full">
              {[40, 65, 90, 115, 140, 165, 190].map((r, ri) => {
                const count = Math.round(r * 0.35);
                return Array.from({ length: count }, (_, i) => {
                  const angle = (i / count) * Math.PI * 2 + ri * 0.3;
                  const x = 200 + r * Math.cos(angle);
                  const y = 200 + r * Math.sin(angle);
                  const dotR = 1.2 + ri * 0.15;
                  return (
                    <circle
                      key={`${ri}-${i}`}
                      cx={x}
                      cy={y}
                      r={dotR}
                      fill="#DDDEE1"
                      opacity={0.45 - ri * 0.04}
                    />
                  );
                });
              })}
            </svg>
          </div>
        </div>

        <div
          className="relative z-10 max-w-[520px] mx-auto px-5 pt-5"
          style={{ paddingBottom: 'calc(180px + env(safe-area-inset-bottom, 0px))' }}
        >
          {/* Hero — left-aligned poster style */}
          <section className="py-1 pb-6">
            <span className="font-label text-[10px] font-bold uppercase tracking-[0.22em] text-ink-faint block mb-2">
              membership
            </span>
            <h1
              className="font-headline font-black text-ink lowercase leading-[0.88] mb-4"
              style={{
                fontSize: 'clamp(44px, 11vw, 60px)',
                letterSpacing: '0.005em',
              }}
            >
              join the<br/>community
            </h1>
            <p className="font-body text-[15px] text-ink-mid leading-snug max-w-[420px] normal-case">
              join the discord for free, or back uzplaylists monthly for exclusive perks.
            </p>
          </section>

          {/* Info banner */}
          <div className="bg-white-cloud border-[1.5px] border-white-fog rounded-[14px] px-[18px] py-4 flex gap-3.5 items-center mb-6">
            <div
              className="w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0"
              style={{ backgroundColor: 'rgba(88, 101, 242, 0.08)', color: '#5865F2' }}
            >
              <FaDiscord size={20} />
            </div>
            <div className="flex-1 min-w-0">
              <div className="font-body font-extrabold text-[15px] text-ink tracking-tight mb-0.5 normal-case">
                Discord access on every tier
              </div>
              <div className="font-body text-[13px] text-ink-mid leading-snug normal-case">
                we auto-link your account after checkout
              </div>
            </div>
          </div>

          {/* Tier list */}
          <div className="flex flex-col gap-3">
            {TIERS.map((tier) => {
              const isSelected = tier.key === selectedKey;
              return (
                <button
                  key={tier.key}
                  type="button"
                  onClick={() => setSelectedKey(tier.key)}
                  className={`relative w-full bg-white-pure border-[1.5px] rounded-[18px] px-[22px] py-5 flex gap-4 items-start cursor-pointer transition-colors text-left ${
                    isSelected
                      ? 'border-brand-purple'
                      : 'border-white-fog hover:border-white-ash'
                  }`}
                  style={isSelected ? { backgroundColor: 'rgba(88, 101, 242, 0.08)' } : {}}
                >
                  {/* Radio selector (left) */}
                  <div
                    className={`w-6 h-6 rounded-full border-2 flex items-center justify-center flex-shrink-0 mt-[3px] transition-colors ${
                      isSelected ? 'bg-brand-purple border-brand-purple' : 'bg-white-pure border-white-ash'
                    }`}
                  >
                    {isSelected && (
                      <div className="w-2 h-2 rounded-full bg-white-pure" />
                    )}
                  </div>

                  <div className="flex-1 min-w-0">
                    {/* Name + badge row */}
                    <div className="flex items-center justify-between gap-2.5 mb-1.5">
                      <div
                        className="font-body font-black text-[24px] text-ink lowercase"
                        style={{ letterSpacing: '-0.02em' }}
                      >
                        {tier.name}
                      </div>
                      {tier.badge && (
                        <span
                          className={`font-label text-[9px] font-bold uppercase tracking-[0.18em] text-white-pure px-2 py-1 rounded flex-shrink-0 ${
                            tier.featured ? 'bg-brand-purple' : 'bg-ink'
                          }`}
                        >
                          {tier.badge}
                        </span>
                      )}
                    </div>

                    {/* Price */}
                    <div className="font-body text-[14px] text-ink-mid font-medium mb-3 normal-case">
                      <strong className="text-ink font-extrabold">{tier.price}</strong>{' '}
                      {tier.priceSuffix && (tier.priceSuffix.startsWith('/') ? tier.priceSuffix : `· ${tier.priceSuffix}`)}
                    </div>

                    {/* Feature pills */}
                    <ul className="flex flex-wrap gap-x-3 gap-y-1 list-none">
                      {tier.features.map((feat, i) => (
                        <li
                          key={i}
                          className="font-label text-[10px] font-bold uppercase tracking-[0.1em] text-ink-mid relative pl-2.5"
                        >
                          <span
                            className={`absolute left-0 top-[5px] w-1 h-1 rounded-full ${
                              isSelected ? 'bg-brand-purple' : 'bg-ink-faint'
                            }`}
                          />
                          {feat}
                        </li>
                      ))}
                    </ul>
                  </div>
                </button>
              );
            })}
          </div>
        </div>

        {/* Sticky bottom CTA bar */}
        <div
          className="fixed left-0 right-0 bottom-0 bg-white-pure border-t-[1.5px] border-white-fog z-10"
          style={{ padding: '18px 20px calc(20px + env(safe-area-inset-bottom, 0px)) 20px' }}
        >
          <div className="max-w-[480px] mx-auto">
            <button
              onClick={handleCTA}
              disabled={isProcessing}
              className="font-label w-full py-[18px] rounded-[16px] border-[1.5px] border-ink bg-ink text-white-pure text-[12px] font-bold uppercase tracking-[0.16em] transition-colors hover:bg-ink-soft disabled:opacity-60 disabled:cursor-not-allowed flex items-center justify-center gap-2.5"
            >
              {isProcessing ? (
                <>
                  <span
                    className="inline-block w-3 h-3 rounded-full border-2 border-white-pure/30"
                    style={{ borderTopColor: 'white', animation: 'spin 0.8s linear infinite' }}
                  />
                  redirecting…
                </>
              ) : (
                <>
                  {selectedTier.ctaIcon === 'discord' && <FaDiscord size={18} />}
                  {selectedTier.cta}
                </>
              )}
            </button>
            {error ? (
              <div className="text-[11px] text-brand-red text-center mt-2.5 leading-snug normal-case">
                {error}
              </div>
            ) : (
              <div className="font-body text-[11px] text-ink-mid text-center mt-2.5 leading-snug normal-case">
                {selectedTier.key === 'free' ? 'no card needed' : 'cancel anytime · auto-link discord'}
                {' · '}
                <a href="/terms" className="underline hover:text-ink">terms</a>
                {' · '}
                <a href="/privacy" className="underline hover:text-ink">privacy</a>
              </div>
            )}
          </div>
        </div>
      </div>

    </>
  );
};

export default PricingPage;
